import { Badge } from "@/components/ui/badge";
import { fmtEUR } from "@/lib/format";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

// Transparent commission breakdown: applied lines, excluded (with reason),
// prüfung flags and payout preconditions. Used by Contracts + Provisionsberechnung.
export function CalcBreakdown({ calc }) {
  if (!calc) return null;
  const pruefung = calc.status === "pruefung";
  return (
    <div className="space-y-3" data-testid="calc-breakdown">
      <div className="flex items-center justify-between rounded-lg border border-border p-3">
        <div className="text-sm font-semibold">Erwartete Provision</div>
        <div className="flex items-center gap-2">
          {pruefung
            ? <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20" data-testid="calc-status">Zur Prüfung</Badge>
            : <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20" data-testid="calc-status">Berechnet</Badge>}
          <span className="text-lg font-semibold" data-testid="calc-total">{fmtEUR(calc.total_amount_eur)}</span>
        </div>
      </div>

      {pruefung && (calc.pruefung_reasons || []).length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-300">
          <div className="mb-1 flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" />Manuelle Prüfung nötig</div>
          <ul className="list-inside list-disc">{calc.pruefung_reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}

      <div>
        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Angewendete Regeln</div>
        <div className="space-y-1.5">
          {(calc.lines || []).map((l, i) => (
            <div key={i} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid="calc-line">
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span>{l.label || l.commission_type}{l.is_recurring && <Badge variant="outline" className="ml-1.5 text-[10px]">wiederkehrend</Badge>}{l.percent ? ` · ${l.percent}%` : ""}</span></div>
              <span className={l.amount_eur < 0 ? "font-medium text-red-600" : "font-medium"}>{fmtEUR(l.amount_eur)}</span>
            </div>
          ))}
          {(calc.lines || []).length === 0 && <div className="text-sm text-muted-foreground">Keine anwendbaren Regeln.</div>}
        </div>
      </div>

      {(calc.excluded || []).length > 0 && (
        <div>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ausgeschlossene Boni</div>
          <div className="space-y-1.5">
            {calc.excluded.map((e, i) => (
              <div key={i} className="flex items-start justify-between gap-3 rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-sm" data-testid="calc-excluded">
                <div className="flex items-start gap-2"><XCircle className="mt-0.5 h-4 w-4 text-muted-foreground" />
                  <span>{e.label || e.commission_type}<span className="block text-xs text-muted-foreground">{e.reason}</span></span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(calc.payout_notes || []).length > 0 && (
        <div className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground">
          Auszahlungsvoraussetzungen: {calc.payout_notes.join("; ")}
        </div>
      )}
    </div>
  );
}
